import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, MapPin, Trash2, Save, Move, Upload, FileImage,
  ArrowRightLeft, Pencil, ExternalLink, Loader2, X,
} from 'lucide-react';

const PLOT_STATUSES = [
  { value: 'available', label: 'Available', color: 'bg-green-500' },
  { value: 'reserved', label: 'Reserved', color: 'bg-yellow-500' },
  { value: 'sold', label: 'Sold', color: 'bg-red-500' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-orange-500' },
];

const BOOKING_STATUSES = ['booked', 'confirmed', 'pending', 'cancelled'];
const PAYMENT_STATUSES = ['pending', 'partial', 'paid', 'refunded'];

const colorFor = (s: string) => PLOT_STATUSES.find(p => p.value === s)?.color || 'bg-gray-500';

interface Props { projectId: string }

export function PlotMapEditor({ projectId }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState(false);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [editPlot, setEditPlot] = useState<any>(null);
  const [bookingDrawer, setBookingDrawer] = useState<{ plot: any; mode: 'modify' | 'transfer' } | null>(null);
  const [uploadingFloorPlan, setUploadingFloorPlan] = useState(false);

  const { data: plots = [] } = useQuery({
    queryKey: ['project-plots', projectId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_plots' as any)
        .select('*, bookings(*)')
        .eq('project_id', projectId)
        .order('created_at');
      if (error) throw error;
      return data as any[];
    },
    enabled: !!projectId,
  });

  const { data: bookings = [] } = useQuery({
    queryKey: ['bookings-for-plot-link', projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id, customer_name, plot_number')
        .eq('project_id', projectId);
      return data || [];
    },
    enabled: !!projectId,
  });

  const addPlot = useMutation({
    mutationFn: async () => {
      const nextNum = (plots.length || 0) + 1;
      const { error } = await supabase.from('project_plots' as any).insert({
        project_id: projectId, label: `Plot ${nextNum}`,
        x_percent: 50, y_percent: 50, status: 'available', created_by: user!.id,
      });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-plots', projectId] }); toast.success('Plot added'); },
    onError: (e: any) => toast.error(e.message),
  });

  const updatePlot = useMutation({
    mutationFn: async (plot: any) => {
      const { id, bookings: _b, ...rest } = plot;
      const { error } = await supabase.from('project_plots' as any).update(rest).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['project-plots', projectId] }),
    onError: (e: any) => toast.error(e.message),
  });

  const deletePlot = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('project_plots' as any).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project-plots', projectId] }); toast.success('Plot deleted'); },
  });

  const updateBooking = useMutation({
    mutationFn: async (b: any) => {
      const { id, ...rest } = b;
      const { error } = await supabase.from('bookings').update(rest).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
      qc.invalidateQueries({ queryKey: ['bookings-for-plot-link', projectId] });
      toast.success('Booking updated');
      setBookingDrawer(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const transferBooking = useMutation({
    mutationFn: async ({ bookingId, toPlotId, notes }: { bookingId: string; toPlotId: string; notes?: string }) => {
      const { error } = await supabase.rpc('transfer_booking' as any, {
        _booking_id: bookingId, _to_plot_id: toPlotId, _notes: notes || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
      qc.invalidateQueries({ queryKey: ['bookings-for-plot-link', projectId] });
      toast.success('Booking transferred');
      setBookingDrawer(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const cancelBooking = useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: string; reason: string }) => {
      const { error } = await supabase.rpc('cancel_booking' as any, {
        _booking_id: bookingId, _reason: reason,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
      toast.success('Booking cancelled');
      setBookingDrawer(null);
    },
    onError: (e: any) => toast.error(e.message),
  });

  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingId || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    qc.setQueryData(['project-plots', projectId], (old: any[] = []) =>
      old.map(p => p.id === draggingId ? { ...p, x_percent: x, y_percent: y } : p));
  };

  const onPointerUp = () => {
    if (draggingId) {
      const plot = plots.find(p => p.id === draggingId);
      if (plot) updatePlot.mutate({ id: plot.id, x_percent: plot.x_percent, y_percent: plot.y_percent });
    }
    setDraggingId(null);
  };

  const uploadFloorPlan = async (plot: any, file: File) => {
    if (!file) return;
    try {
      setUploadingFloorPlan(true);
      const ext = file.name.split('.').pop() || 'png';
      const path = `floor-plans/${projectId}/${plot.id}-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from('project-images').upload(path, file, { upsert: true });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from('project-images').getPublicUrl(path);
      await supabase.from('project_plots' as any).update({ floor_plan_url: pub.publicUrl }).eq('id', plot.id);
      qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
      setEditPlot((cur: any) => cur && cur.id === plot.id ? { ...cur, floor_plan_url: pub.publicUrl } : cur);
      toast.success('Floor plan uploaded');
    } catch (e: any) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploadingFloorPlan(false);
    }
  };

  const removeFloorPlan = async (plot: any) => {
    await supabase.from('project_plots' as any).update({ floor_plan_url: null }).eq('id', plot.id);
    qc.invalidateQueries({ queryKey: ['project-plots', projectId] });
    setEditPlot((cur: any) => cur && cur.id === plot.id ? { ...cur, floor_plan_url: null } : cur);
    toast.success('Floor plan removed');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Button size="sm" variant={editMode ? 'default' : 'outline'} onClick={() => setEditMode(v => !v)} className="gap-1">
            <Move className="h-4 w-4" /> {editMode ? 'Editing — Click to exit' : 'Edit Map'}
          </Button>
          {editMode && (
            <Button size="sm" variant="outline" onClick={() => addPlot.mutate()} className="gap-1">
              <Plus className="h-4 w-4" /> Add Plot
            </Button>
          )}
        </div>
        <div className="flex gap-3 text-xs">
          {PLOT_STATUSES.map(s => (
            <div key={s.value} className="flex items-center gap-1">
              <div className={`h-3 w-3 rounded-full ${s.color}`} />
              <span className="text-muted-foreground">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      <div ref={containerRef} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
        className="relative w-full aspect-[16/9] rounded-2xl border-2 border-dashed bg-gradient-to-br from-emerald-50 to-blue-50 overflow-hidden select-none"
        style={{ backgroundImage: 'repeating-linear-gradient(0deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(0,0,0,0.04) 0 1px, transparent 1px 40px)' }}>
        {plots.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
            {editMode ? 'Click "Add Plot" to place markers on the map.' : 'No plots placed yet.'}
          </div>
        )}
        {plots.map((p: any) => (
          <div key={p.id} className="absolute -translate-x-1/2 -translate-y-full" style={{ left: `${p.x_percent}%`, top: `${p.y_percent}%` }}>
            <button
              onPointerDown={(e) => { if (editMode) { e.preventDefault(); setDraggingId(p.id); } }}
              onClick={() => !draggingId && setEditPlot(p)}
              className={`group flex flex-col items-center ${editMode ? 'cursor-move' : 'cursor-pointer'}`}>
              <div className="px-2 py-0.5 mb-0.5 rounded bg-white shadow text-[10px] font-bold border whitespace-nowrap flex items-center gap-1">
                {p.label}
                {p.floor_plan_url && <FileImage className="h-2.5 w-2.5 text-blue-600" />}
              </div>
              <MapPin className={`h-7 w-7 ${colorFor(p.status).replace('bg-', 'text-')} drop-shadow-md`} fill="currentColor" />
            </button>
          </div>
        ))}
      </div>

      {/* Plot edit dialog */}
      <Dialog open={!!editPlot} onOpenChange={(o) => !o && setEditPlot(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Plot</DialogTitle></DialogHeader>
          {editPlot && (
            <Tabs defaultValue="info">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="info">Plot Info</TabsTrigger>
                <TabsTrigger value="floor">Floor Plan</TabsTrigger>
                <TabsTrigger value="booking">Booking</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-3 pt-3">
                <div>
                  <Label className="text-xs">Label *</Label>
                  <Input className="h-9 text-sm" value={editPlot.label}
                    onChange={e => setEditPlot({ ...editPlot, label: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">X (%)</Label>
                    <Input type="number" min={0} max={100} step="0.1" className="h-9 text-sm"
                      value={editPlot.x_percent}
                      onChange={e => setEditPlot({ ...editPlot, x_percent: Number(e.target.value) })} />
                  </div>
                  <div>
                    <Label className="text-xs">Y (%)</Label>
                    <Input type="number" min={0} max={100} step="0.1" className="h-9 text-sm"
                      value={editPlot.y_percent}
                      onChange={e => setEditPlot({ ...editPlot, y_percent: Number(e.target.value) })} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={editPlot.status} onValueChange={v => setEditPlot({ ...editPlot, status: v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PLOT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Linked Booking</Label>
                  <Select value={editPlot.booking_id || 'none'}
                    onValueChange={v => setEditPlot({ ...editPlot, booking_id: v === 'none' ? null : v })}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {bookings.map((b: any) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.customer_name} {b.plot_number ? `— Plot ${b.plot_number}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Notes</Label>
                  <Textarea rows={2} className="text-sm" value={editPlot.notes || ''}
                    onChange={e => setEditPlot({ ...editPlot, notes: e.target.value })} />
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 gap-1" disabled={updatePlot.isPending}
                    onClick={() => updatePlot.mutate(editPlot, {
                      onSuccess: () => { toast.success('Plot updated'); setEditPlot(null); }
                    })}>
                    <Save className="h-4 w-4" /> Save
                  </Button>
                  <Button variant="destructive" size="icon"
                    onClick={() => { if (confirm('Delete this plot?')) { deletePlot.mutate(editPlot.id); setEditPlot(null); } }}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="floor" className="space-y-3 pt-3">
                {editPlot.floor_plan_url ? (
                  <div className="space-y-2">
                    <div className="rounded border overflow-hidden bg-muted">
                      <img src={editPlot.floor_plan_url} alt="Floor plan" className="w-full max-h-72 object-contain" />
                    </div>
                    <div className="flex gap-2">
                      <Button asChild variant="outline" size="sm" className="flex-1 gap-1">
                        <a href={editPlot.floor_plan_url} target="_blank" rel="noreferrer">
                          <ExternalLink className="h-3.5 w-3.5" /> Open full size
                        </a>
                      </Button>
                      <label className="flex-1">
                        <input type="file" accept="image/*,application/pdf" hidden
                          disabled={uploadingFloorPlan}
                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadFloorPlan(editPlot, f); e.target.value = ''; }} />
                        <Button variant="outline" size="sm" className="w-full gap-1 cursor-pointer" asChild>
                          <span>{uploadingFloorPlan ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Replace</span>
                        </Button>
                      </label>
                      <Button variant="destructive" size="sm" onClick={() => removeFloorPlan(editPlot)} className="gap-1">
                        <X className="h-3.5 w-3.5" /> Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-8 cursor-pointer hover:bg-muted/50">
                    <input type="file" accept="image/*,application/pdf" hidden disabled={uploadingFloorPlan}
                      onChange={e => { const f = e.target.files?.[0]; if (f) uploadFloorPlan(editPlot, f); e.target.value = ''; }} />
                    {uploadingFloorPlan ? <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /> : <Upload className="h-8 w-8 text-muted-foreground" />}
                    <div className="text-sm font-medium">Upload floor plan for {editPlot.label}</div>
                    <div className="text-xs text-muted-foreground">PNG, JPG or PDF up to 10MB</div>
                  </label>
                )}
              </TabsContent>

              <TabsContent value="booking" className="space-y-3 pt-3">
                {editPlot.bookings ? (
                  <div className="space-y-3">
                    <div className="rounded border p-3 bg-muted/40 space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <strong>{editPlot.bookings.customer_name}</strong>
                        <Badge variant="secondary">{editPlot.bookings.booking_status}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground">{editPlot.bookings.customer_phone}</div>
                      {editPlot.bookings.booking_amount && (
                        <div className="text-xs">Amount: ETB {Number(editPlot.bookings.booking_amount).toLocaleString()}</div>
                      )}
                      <div className="text-xs">Payment: {editPlot.bookings.payment_status}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="flex-1 gap-1"
                        onClick={() => { setBookingDrawer({ plot: editPlot, mode: 'modify' }); setEditPlot(null); }}>
                        <Pencil className="h-3.5 w-3.5" /> Modify
                      </Button>
                      <Button size="sm" variant="outline" className="flex-1 gap-1"
                        onClick={() => { setBookingDrawer({ plot: editPlot, mode: 'transfer' }); setEditPlot(null); }}>
                        <ArrowRightLeft className="h-3.5 w-3.5" /> Transfer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No booking attached. Use the <strong>Plot Info</strong> tab to link an existing booking.
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      <BookingDrawer
        state={bookingDrawer}
        onClose={() => setBookingDrawer(null)}
        availablePlots={plots.filter((p: any) => !p.booking_id || p.booking_id === bookingDrawer?.plot?.bookings?.id)}
        onUpdate={(b) => updateBooking.mutate(b)}
        onTransfer={(toPlotId, notes) => {
          if (bookingDrawer?.plot?.bookings?.id) {
            transferBooking.mutate({ bookingId: bookingDrawer.plot.bookings.id, toPlotId, notes });
          }
        }}
        onCancel={(reason) => {
          if (bookingDrawer?.plot?.bookings?.id) {
            cancelBooking.mutate({ bookingId: bookingDrawer.plot.bookings.id, reason });
          }
        }}
        isPending={updateBooking.isPending || transferBooking.isPending || cancelBooking.isPending}
      />
    </div>
  );
}

function BookingDrawer({
  state, onClose, availablePlots, onUpdate, onTransfer, onCancel, isPending,
}: {
  state: { plot: any; mode: 'modify' | 'transfer' } | null;
  onClose: () => void;
  availablePlots: any[];
  onUpdate: (b: any) => void;
  onTransfer: (toPlotId: string, notes: string) => void;
  onCancel: (reason: string) => void;
  isPending: boolean;
}) {
  const booking = state?.plot?.bookings;
  const [form, setForm] = useState<any>(booking || {});
  const [toPlotId, setToPlotId] = useState<string>('');
  const [transferNotes, setTransferNotes] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  // Reset when drawer opens
  if (state && booking && form.id !== booking.id) {
    setForm(booking);
    setToPlotId('');
    setTransferNotes('');
    setCancelReason('');
  }

  if (!state || !booking) return null;

  return (
    <Sheet open={!!state} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{state.mode === 'transfer' ? 'Transfer Booking' : 'Modify Booking'}</SheetTitle>
          <SheetDescription>
            {booking.customer_name} — currently on <strong>{state.plot.label}</strong>
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue={state.mode} className="mt-4">
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="modify">Modify</TabsTrigger>
            <TabsTrigger value="transfer">Transfer</TabsTrigger>
            <TabsTrigger value="cancel">Cancel</TabsTrigger>
          </TabsList>

          <TabsContent value="modify" className="space-y-3 pt-4">
            <div>
              <Label className="text-xs">Customer Name</Label>
              <Input className="h-9" value={form.customer_name || ''} onChange={e => setForm({ ...form, customer_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input className="h-9" value={form.customer_phone || ''} onChange={e => setForm({ ...form, customer_phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input className="h-9" type="email" value={form.customer_email || ''} onChange={e => setForm({ ...form, customer_email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Booking Status</Label>
                <Select value={form.booking_status} onValueChange={v => setForm({ ...form, booking_status: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {BOOKING_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payment</Label>
                <Select value={form.payment_status} onValueChange={v => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Booking Amount (ETB)</Label>
              <Input className="h-9" type="number" value={form.booking_amount ?? ''}
                onChange={e => setForm({ ...form, booking_amount: e.target.value === '' ? null : Number(e.target.value) })} />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            <Button className="w-full gap-1" disabled={isPending}
              onClick={() => onUpdate({
                id: booking.id,
                customer_name: form.customer_name,
                customer_phone: form.customer_phone,
                customer_email: form.customer_email,
                booking_status: form.booking_status,
                payment_status: form.payment_status,
                booking_amount: form.booking_amount,
                notes: form.notes,
              })}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
            </Button>
          </TabsContent>

          <TabsContent value="transfer" className="space-y-3 pt-4">
            <div className="text-xs text-muted-foreground">
              Move this booking to another plot in the same project. The current plot will become available.
            </div>
            <div>
              <Label className="text-xs">Destination Plot</Label>
              <Select value={toPlotId} onValueChange={setToPlotId}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Choose a plot..." /></SelectTrigger>
                <SelectContent>
                  {availablePlots
                    .filter(p => p.id !== state.plot.id)
                    .map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.label} — {p.status}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Transfer Notes</Label>
              <Textarea rows={3} value={transferNotes} onChange={e => setTransferNotes(e.target.value)}
                placeholder="Why is this booking being transferred?" />
            </div>
            <Button className="w-full gap-1" disabled={!toPlotId || isPending}
              onClick={() => onTransfer(toPlotId, transferNotes)}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
              Transfer Booking
            </Button>
          </TabsContent>

          <TabsContent value="cancel" className="space-y-3 pt-4">
            <div className="text-xs text-muted-foreground">
              Cancel this booking and release the plot back to inventory.
            </div>
            <div>
              <Label className="text-xs">Cancellation Reason</Label>
              <Textarea rows={3} value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder="Reason..." />
            </div>
            <Button variant="destructive" className="w-full gap-1" disabled={!cancelReason.trim() || isPending}
              onClick={() => { if (confirm('Cancel this booking?')) onCancel(cancelReason); }}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
              Cancel Booking
            </Button>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
